import {
  DropRectangle,
  ERectangleIdentifiers,
  RMRectangle,
  WellRectangle,
} from 'business/models/CustomModels';
import { WellIngDTO } from 'business/models/Models';
import WellRect from 'business/models/WellRect';
import getSlantyLinesCoordinates from 'components/commandpanel/FragmentLayer/SlantyLinesDesign';
import { GetIngredients, RoundValue, getHexColor } from 'utils/Global';

const ingredientsTypeColorsDictionary: { [key: number]: string } = {};
const getIngredientsTypeColorByIngredientTypeId = (typeId: number) => {
  if (ingredientsTypeColorsDictionary[typeId]) {
    return ingredientsTypeColorsDictionary[typeId];
  }

  const ingredientsLookUp = GetIngredients();
  const { ingredientTypeId } = ingredientsLookUp.ingredientIngredientType.find(
    (i) => i.id == typeId,
  );
  const ingType = ingredientsLookUp.ingredientType.find((i) => i.id == ingredientTypeId);
  const ingColor = ingredientsLookUp.ingredientTypeColor.find(
    (i) => i.id == ingType.ingredientTypeColorId,
  );
  ingredientsTypeColorsDictionary[typeId] = getHexColor(ingColor.color);

  return ingredientsTypeColorsDictionary[typeId];
};

export default function CalculateWellRects(
  well,
  wellRectangle,
  maxWellVolume,
  dropsList = [],
  strokeNormalizer: number,
) {
  const rects: WellRect = new WellRect();

  const numDropRects = 4;
  const strokeWidth = 0.35 / strokeNormalizer;
  const dropColorProtein = '#008000';
  const dropColorWell = '#9ACD32';
  const dropColorIngredients = '#FFA500';
  const dropColorSeed = '#E016E6';
  const waterColor = '#37CCEC';
  const dropFragmentColor = '#5100CC';
  const DropRectHeightRatio = 0.12;
  const RectSeparatorHeightRatio = 0.04;
  const BufferRectHeightRatio = 0.12;
  const IngredientRectHeightRatio = 1 - DropRectHeightRatio - BufferRectHeightRatio;

  /**
   * we need to clone this, Since ContainerDropList performs sort operations and tries to modify the same variable which will throw error.
   */
  const ContainerDropList = [...dropsList];
  //   let DropRectHeightRatio = 0.12
  const dropRectangleSeparator = 0.1;
  const AdjustedDropRectHeight = DropRectHeightRatio - RectSeparatorHeightRatio;
  const MaxWellVolume = maxWellVolume;

  function PaintWell(well: WellIngDTO, cellRectangle) {
    try {
      //const rectsStartIndexCurrentWell = rects.length;

      const emptyRectangle = GetEmptyRectangle(well, cellRectangle);
      // const erroneousWellLayers = getErroneusWellLayers(well);
      // const erroneousDropLayers = getErroneusDropLayers(well);

      if (well.hasError) {
        PaintEmptyRectangle(well, cellRectangle, emptyRectangle);
        PaintWellErrorRectangle(cellRectangle, emptyRectangle, strokeNormalizer);
        return rects;
      }
      //            return rects
      for (let i = 0; i < well.wellLayerList.length; i++) {
        PaintWaterRectangle(well, cellRectangle, emptyRectangle);
        PaintIngredientsRectangles(well, cellRectangle, emptyRectangle);
        PaintBufferRectangle(well, cellRectangle, emptyRectangle);
      }
      PaintEmptyRectangle(well, cellRectangle, emptyRectangle);
      PaintWellContentEmptyRectangle(cellRectangle, emptyRectangle);
      PaintDrop(well, cellRectangle);
      PaintEmptyBufferRectangle(cellRectangle, emptyRectangle);

      return rects;
      // wells[row][col] = {Well:well, CellRect:cellRectangle, WellRects:wellRects }
    } catch (err) {
      console.error(`---------EXCEPTION (PaintWell)---------${err}`, err);
    }
  }

  function GetEmptyRectangle(well, cellRectangle): RMRectangle {
    let wellVol = 0;
    if (well.wellVolume) {
      wellVol = well.wellVolume;
    }

    const emptyRectangleHeight =
      ((MaxWellVolume - wellVol) / MaxWellVolume) *
      cellRectangle.h *
      IngredientRectHeightRatio;

    return {
      x: cellRectangle.x,
      y: cellRectangle.y + cellRectangle.h * DropRectHeightRatio,
      w: cellRectangle.w,
      h: emptyRectangleHeight,
    };
  }

  function PaintDrop(well, cellRectangle) {
    PaintDropRectangles(well, cellRectangle);
  }

  function PaintDropRectangles(well, cellRectangle) {
    if (ContainerDropList?.length === 0) {
      return;
    }

    let separator = 0;
    if (ContainerDropList?.length >= 2) {
      separator = cellRectangle.w * dropRectangleSeparator;
    } else {
      separator = 0;
    }

    const dropRectangleWidth =
      (cellRectangle.w - (ContainerDropList?.length - 1) * separator) /
      ContainerDropList?.length;
    const dropRectangle: DropRectangle = {
      x: cellRectangle.x,
      y: cellRectangle.y,
      w: dropRectangleWidth,
      h: cellRectangle.h * AdjustedDropRectHeight,
      bColor: 'black',
      identifier: ERectangleIdentifiers.Drop,
    };

    // PaintRectangle(rectKey, dropRectangle, "black")

    ContainerDropList?.sort((a, b) => a.dropNumber - b.dropNumber).forEach(
      (containerDrop, i) => {
        const drop = well.dropList.find(
          (drop) => drop.dropNumber === containerDrop.dropNumber,
        );
        // rects.drop[i] = []
        if (drop) {
          const {
            dropWellRect,
            dropProteinRect,
            dropSeedRect,
            dropIngredientsRect,
            dropFragmentRect,
          } = CalculateSubDropRecangles(dropRectangle, drop);

          rects.drop[drop.dropNumber - 1] = [];
          rects.drop[drop.dropNumber - 1].push({ ...dropWellRect, strokeWidth });
          rects.drop[drop.dropNumber - 1].push({ ...dropProteinRect, strokeWidth });
          rects.drop[drop.dropNumber - 1].push({ ...dropSeedRect, strokeWidth });
          rects.drop[drop.dropNumber - 1].push({ ...dropFragmentRect, strokeWidth });
          if (dropIngredientsRect.w > 0) {
            rects.drop[drop.dropNumber - 1].push({
              ...dropIngredientsRect,
              strokeWidth,
            });
          }
        }
        if (rects.drop[i] === undefined) {
          rects.drop[i] = [];
        }
        rects.drop[i].push({
          ...dropRectangle,
          strokeWidth,
          dropNumber: drop.dropNumber,
        });
        dropRectangle.x += dropRectangleWidth + separator;
      },
    );
  }

  function PaintWaterRectangle(well, cellRectangle, emptyRectangle) {
    const waterRectangle = GetWaterRectangle(well, cellRectangle, emptyRectangle);
    waterRectangle.bColor = 'black';
    waterRectangle.fillColor = waterColor;
    waterRectangle.type = 'water';
    waterRectangle.ingredientStockId = undefined;

    rects.ing.push({ ...waterRectangle, strokeWidth });

    //        PaintRectangle(well.WellId + 'w', waterRectangle, "black", waterColor)
  }

  function PaintIngredientsRectangles(well, cellRectangle, emptyRectangle) {
    const ingredientRectangle = GetIngredientRectangle(
      well,
      cellRectangle,
      emptyRectangle,
    );
    DrawIngredientsRectangle(well, ingredientRectangle);
  }

  function PaintBufferRectangle(well, cellRectangle, emptyRectangle) {
    let bufferCount = 0;
    for (let i = 0; i < well.wellLayerList.length; i++) {
      const wellLayer = well.wellLayerList[i];

      for (let j = 0; j < wellLayer.wellLayerIngredientList.length; j++) {
        const wellLayerIngredient = wellLayer.wellLayerIngredientList[j];
        if (wellLayerIngredient.ingredientTypeIsBuffer) {
          bufferCount++;
        }
      }
    }

    if (bufferCount === 1) {
      const bufferWhiteRectangle = GetBufferWhiteRectangle(
        well,
        cellRectangle,
        emptyRectangle,
      );

      if (bufferWhiteRectangle.w !== cellRectangle.w) {
        const bufferBlackRectangle: WellRectangle = {
          x: bufferWhiteRectangle.x + bufferWhiteRectangle.w,
          y: bufferWhiteRectangle.y,
          w: cellRectangle.w - bufferWhiteRectangle.w,
          h: cellRectangle.h * BufferRectHeightRatio,
          bColor: 'black',
          fillColor: 'black',
          type: 'buffer',
          ingredientStockId: undefined,
        };
        rects.ing.push({ ...bufferBlackRectangle, strokeWidth });

        // PaintRectangle(well.WellLayerIngredientId + "bufferBlackRectangle", bufferBlackRectangle, "black", "black")
      }
    } else if (bufferCount > 1) {
      // debugger
      const bufferTextureRectangle = GetBufferRectangle(
        well,
        cellRectangle,
        emptyRectangle,
      );
      const bufferRectLines = getSlantyLinesCoordinates({
        rectX: bufferTextureRectangle.x,
        rectY: bufferTextureRectangle.y,
        rectWidth: bufferTextureRectangle.w,
        rectHeight: bufferTextureRectangle.h,
        gap: bufferTextureRectangle.w / 6,
      });
      rects.ing.push({
        ...bufferTextureRectangle,
        bColor: 'black',
        fillColor: 'lightgray',
        strokeWidth,
      });
      rects.ing.push(...[].concat(...bufferRectLines));
      //   PaintRectangle(well.WellLayerIngredientId + "bufferTextureRectangle", bufferTextureRectangle, "black", "lightgray")
      // graphics.FillRectangle(new HatchBrush(HatchStyle.ForwardDiagonal, Color.FromArgb(alphaValue, Color.Black), Color.FromArgb(alphaValue, Color.LightGray)), bufferTextureRectangle);
      // PaintRectangleF(graphics, bufferTextureRectangle, Color.Black);
    }
  }

  function PaintEmptyRectangle(well, cellRectangle, emptyRectangle) {
    rects.wells.push({ ...cellRectangle, bcolor: 'black', strokeWidth });
    // RMRect.Paint(rects, cellRectangle, 0.5, "black")
  }

  function PaintWellContentEmptyRectangle(cellRectangle, emptyRectangle) {
    const wellContentHeight = cellRectangle.h * IngredientRectHeightRatio;
    const wellContentRect: RMRectangle = {
      x: emptyRectangle.x,
      y: emptyRectangle.y,
      w: emptyRectangle.w,
      h: wellContentHeight,
    };
    rects.wells.push({ ...wellContentRect, bColor: 'black', strokeWidth });
  }

  function GetBufferWhiteRectangle(well, cellRectangle, emptyRectangle): RMRectangle {
    const ingredientRectangle = GetIngredientRectangle(
      well,
      cellRectangle,
      emptyRectangle,
    );
    for (let i = 0; i < well.wellLayerList.length; i++) {
      const wellLayer = well.wellLayerList[i];

      for (let j = 0; j < wellLayer.wellLayerIngredientList.length; j++) {
        const wellLayerIngredient = wellLayer.wellLayerIngredientList[j];

        if (wellLayerIngredient.ingredientTypeIsBuffer) {
          const lowVol = wellLayerIngredient.lowPHVolume;
          if (wellLayerIngredient.highPHVolume) {
            const highVol = wellLayerIngredient.highPHVolume;
            let width = 0;
            width = cellRectangle.w;
            if (lowVol + highVol !== 0 && highVol >= 0) {
              width = (lowVol / (lowVol + highVol)) * cellRectangle.w;
            }
            return {
              x: ingredientRectangle.x,
              y: ingredientRectangle.y + ingredientRectangle.h,
              w: width,
              h: cellRectangle.h * BufferRectHeightRatio,
            };
          }
        }
      }
    }

    return {
      x: ingredientRectangle.x,
      y: ingredientRectangle.y + ingredientRectangle.h,
      w: ingredientRectangle.w,
      h: cellRectangle.h * BufferRectHeightRatio,
    };
  }

  function GetBufferRectangle(well, cellRectangle, emptyRectangle): RMRectangle {
    const ingredientRectangle = GetIngredientRectangle(
      well,
      cellRectangle,
      emptyRectangle,
    );
    return {
      x: cellRectangle.x,
      y: ingredientRectangle.y + ingredientRectangle.h,
      w: cellRectangle.w,
      h: cellRectangle.h * BufferRectHeightRatio,
    };
  }

  function GetIngredientRectangle(well, cellRectangle, emptyRectangle): WellRectangle {
    const waterRectangle = GetWaterRectangle(well, cellRectangle, emptyRectangle);
    return {
      x: waterRectangle.x,
      y: waterRectangle.y + waterRectangle.h,
      w: waterRectangle.w,
      h:
        cellRectangle.h * IngredientRectHeightRatio - emptyRectangle.h - waterRectangle.h,
    };
  }

  function GetWaterRectangle(well, cellRectangle, emptyRectangle): WellRectangle {
    let waterVol = 0;
    if (well.wellWaterVolume) {
      waterVol = well.wellWaterVolume;
    }

    if (waterVol > MaxWellVolume) {
      waterVol = MaxWellVolume;
    }

    const waterRectangleHeight =
      (waterVol / MaxWellVolume) * cellRectangle.h * IngredientRectHeightRatio;

    return {
      x: emptyRectangle.x,
      y: emptyRectangle.y + emptyRectangle.h,
      w: emptyRectangle.w,
      h: waterRectangleHeight,
    };
  }

  function CalculateSubDropRecangles(dropRectangle, drop) {
    const dropIngredientsVolume = GetWellDropIngredientsVolume(drop);
    const totalDropVolume =
      drop.dropProteinVolume +
      drop.dropWellVolume +
      drop.dropSeedVolume +
      drop.dropFragmentVolume +
      dropIngredientsVolume;
    let dropWellRectWidth;
    let dropProteinRectWidth;
    let dropSeedRectWidth;
    let dropFragmentsRectWidth;
    let dropIngredientsRectWidth;

    if (
      drop.dropProteinVolume < 0 ||
      drop.dropWellVolume < 0 ||
      drop.dropSeedVolume < 0 ||
      drop.dropFragmentVolume < 0
    ) {
      dropWellRectWidth = dropRectangle.w / numDropRects;
      dropProteinRectWidth = dropRectangle.w / numDropRects;
      dropSeedRectWidth = dropRectangle.w / numDropRects;
      dropFragmentsRectWidth = dropRectangle.w / numDropRects;
      dropIngredientsRectWidth = 0.0;
    } else if (totalDropVolume === 0) {
      const dropRectWidth = 0;
      dropWellRectWidth = dropRectWidth;
      dropProteinRectWidth = dropRectWidth;
      dropSeedRectWidth = dropRectWidth;
      dropFragmentsRectWidth = dropRectWidth;
      dropIngredientsRectWidth = dropRectWidth;
    } else {
      dropWellRectWidth = (drop.dropWellVolume / totalDropVolume) * dropRectangle.w;
      dropProteinRectWidth = (drop.dropProteinVolume / totalDropVolume) * dropRectangle.w;
      dropSeedRectWidth = (drop.dropSeedVolume / totalDropVolume) * dropRectangle.w;
      dropFragmentsRectWidth =
        (drop.dropFragmentVolume / totalDropVolume) * dropRectangle.w;
      dropIngredientsRectWidth =
        (dropIngredientsVolume / totalDropVolume) * dropRectangle.w;
    }

    const dropWellRect: RMRectangle = {
      x: dropRectangle.x,
      y: dropRectangle.y,
      w: dropWellRectWidth,
      h: dropRectangle.h,
      bColor: dropColorWell,
      fillColor: dropColorWell,
    };
    const dropProteinRect: RMRectangle = {
      x: dropRectangle.x + dropWellRect.w,
      y: dropRectangle.y,
      w: dropProteinRectWidth,
      h: dropRectangle.h,
      bColor: dropColorProtein,
      fillColor: dropColorProtein,
    };
    const dropIngredientsRect: RMRectangle = {
      x: dropRectangle.x + dropWellRect.w + dropProteinRect.w,
      y: dropRectangle.y,
      w: dropIngredientsRectWidth,
      h: dropRectangle.h,
      bColor: dropColorIngredients,
      fillColor: dropColorIngredients,
    };
    const dropSeedRect: RMRectangle = {
      x: dropRectangle.x + dropWellRect.w + dropProteinRect.w + dropIngredientsRect.w,
      y: dropRectangle.y,
      w: dropSeedRectWidth,
      h: dropRectangle.h,
      bColor: dropColorSeed,
      fillColor: dropColorSeed,
    };
    const dropFragmentRect: RMRectangle = {
      x:
        dropRectangle.x +
        dropWellRect.w +
        dropProteinRect.w +
        dropIngredientsRect.w +
        dropSeedRect.w,
      y: dropRectangle.y,
      w: dropFragmentsRectWidth,
      h: dropRectangle.h,
      bColor: dropFragmentColor,
      fillColor: dropFragmentColor,
    };

    return {
      dropWellRect,
      dropProteinRect,
      dropIngredientsRect,
      dropSeedRect,
      dropFragmentRect,
    };
  }

  function GetWellDropIngredientsVolume(drop) {
    let dropIngredientsVolume = drop.volume ? drop.volume : 0;
    dropIngredientsVolume -= drop.dropProteinVolume ? drop.dropProteinVolume : 0;
    dropIngredientsVolume -= drop.dropWellVolume ? drop.dropWellVolume : 0;
    dropIngredientsVolume -= drop.dropSeedVolume ? drop.dropSeedVolume : 0;
    dropIngredientsVolume -= drop.dropFragmentVolume ? drop.dropFragmentVolume : 0;
    return RoundValue(dropIngredientsVolume);
  }

  function DrawIngredientsRectangle(well, ingredientRectangle) {
    let previousY = ingredientRectangle.y;
    let totalIngredientVolume = 0;

    for (let i = 0; i < well.wellLayerList.length; i++) {
      const wellLayer = well.wellLayerList[i];

      for (let j = 0; j < wellLayer.wellLayerIngredientList.length; j++) {
        const wellLayerIngredient = wellLayer.wellLayerIngredientList[j];
        totalIngredientVolume += wellLayerIngredient.volume;
      }
    }

    for (let i = 0; i < well.wellLayerList.length; i++) {
      const wellLayer = well.wellLayerList[i];

      for (let j = 0; j < wellLayer.wellLayerIngredientList.length; j++) {
        const wellLayerIngredient = wellLayer.wellLayerIngredientList[j];

        const vol = wellLayerIngredient.volume;

        const bColor =
          wellLayerIngredient.hexColor ||
          getIngredientsTypeColorByIngredientTypeId(
            wellLayerIngredient.ingredientIngredientTypeId,
          );
        const fillColor = bColor;
        if (totalIngredientVolume !== 0) {
          const w = (vol / totalIngredientVolume) * ingredientRectangle.h;
          const rect: WellRectangle = {
            x: ingredientRectangle.x,
            y: previousY,
            w: ingredientRectangle.w,
            h: w,
            bColor,
            fillColor,
            type: 'ingredient',
            ingredientStockId: wellLayerIngredient?.ingredientStockId,
          };

          rects.ing.push({ ...rect, strokeWidth });

          // PaintRectangle(well.WellId + " - " + wellLayer.WellLayerId + " - " + j,
          //    rect, wellLayerIngredient.HexColor, wellLayerIngredient.HexColor)

          previousY += rect.h;
        }
      }
    }
  }

  function PaintWellErrorRectangle(
    cellRectangle,
    emptyRectangle,
    strokeNormalizer: number,
  ) {
    const dropHeight = cellRectangle.h * AdjustedDropRectHeight;
    const wellContentRect: RMRectangle = {
      x: emptyRectangle.x,
      y: emptyRectangle.y - dropHeight,
      w: emptyRectangle.w,
      h: cellRectangle.h,
    };
    rects.wells.push({
      ...wellContentRect,
      bColor: 'red',
      strokeWidth: 2 / strokeNormalizer,
    });
  }

  function PaintEmptyBufferRectangle(cellRectangle, emptyRectangle) {
    const height = cellRectangle.h * BufferRectHeightRatio;
    const bufferWhiteRectangle: RMRectangle = {
      x: cellRectangle.x,
      y: cellRectangle.y + cellRectangle.h - height,
      w: cellRectangle.w,
      h: height,
    };
    rects.ing.push({ ...bufferWhiteRectangle, bColor: 'black', strokeWidth });
  }

  return PaintWell(well, wellRectangle);
}
